import { isExpression, isDigit, isIdentifierStart, isIdentifierPart } from './expression_export';

// ────────────────────────────────────────────────────────────────────────────
// Expression grammar check (editor-side validation)
//
// DUPLICATION WARNING: this is a hand port of the lexer + recursive-descent
// grammar in `lv_xml_evaluate.c` (the runtime evaluator). It exists because the
// validator is synchronous and runs in contexts with no WASM runtime (the node
// CLI, VS Code, Theia), so it cannot call the C evaluator. Keep the two in sync:
// if you change the grammar, operators, or the diagnostic wording there, mirror
// it here.
//
// Scope: this checks *grammar* only (token shapes + structure), NOT types or
// identifier resolution. Type errors (`100 + 'x'`, mismatched ternary branches,
// `%` on a float, …) and unknown identifiers are still caught at runtime by the
// C evaluator, which logs the specific reason and the whole expression. The
// error strings below intentionally match the C `lv_xml_eval_err_msg()` wording.

type ExprToken =
    | { k: 'num' | 'hex' | 'str' | 'lparen' | 'rparen' }
    | { k: 'ident'; text: string }
    | { k: 'op'; text: string } // unary/arith/ternary/concat: + - * / % ? : . !
    | { k: 'cmp'; text: string }; // comparison: == != < <= > >=

type TokenizeResult = { tokens: ExprToken[] } | { error: string };

const ERR_BAD_HEX: string = 'malformed color/hex literal (expected `0x` followed by hex digits)';
const ERR_UNTERMINATED_STRING: string = "unterminated string literal (missing closing `'`)";
const ERR_BAD_CHAR: string = 'unexpected character (note: use `==` to compare, `.` to concatenate)';
const ERR_EXPECTED_OPERAND: string = 'expected a value (number, string, color, bool, or identifier)';
const ERR_EXPECTED_RPAREN: string = 'missing closing `)`';
const ERR_EXPECTED_COLON: string = 'malformed ternary, expected `:` (the form is `cond ? a : b`)';
const ERR_TRAILING: string = 'unexpected trailing tokens after the expression';
const ERR_EMPTY: string = 'the expression is empty';

// Lexer — mirrors lv_xml_eval_next_token().
const tokenizeExpression = (src: string): TokenizeResult => {
    const tokens: ExprToken[] = [];
    const n: number = src.length;
    let i: number = 0;

    while (i < n) {
        const c: string = src[i];

        if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
            i++;
            continue;
        }

        // `0x…` hex/color literal, else a decimal int/float
        if (isDigit(c)) {
            if (c === '0' && (src[i + 1] === 'x' || src[i + 1] === 'X')) {
                let j: number = i + 2;
                while (j < n && /[0-9a-fA-F]/.test(src[j])) j++;
                if (j === i + 2) return { error: ERR_BAD_HEX };
                tokens.push({ k: 'hex' });
                i = j;
                continue;
            }
            let j: number = i;
            while (j < n && isDigit(src[j])) j++;
            if (src[j] === '.' && isDigit(src[j + 1])) {
                j++;
                while (j < n && isDigit(src[j])) j++;
            }
            tokens.push({ k: 'num' });
            i = j;
            continue;
        }

        // 'single-quoted string'
        if (c === "'") {
            let j: number = i + 1;
            while (j < n && src[j] !== "'") j++;
            if (j >= n) return { error: ERR_UNTERMINATED_STRING };
            tokens.push({ k: 'str' });
            i = j + 1;
            continue;
        }

        if (c === '+' || c === '-' || c === '*' || c === '/' || c === '%' || c === '?' || c === ':' || c === '.') {
            tokens.push({ k: 'op', text: c });
            i++;
            continue;
        }
        if (c === '(') {
            tokens.push({ k: 'lparen' });
            i++;
            continue;
        }
        if (c === ')') {
            tokens.push({ k: 'rparen' });
            i++;
            continue;
        }
        if (c === '=') {
            if (src[i + 1] === '=') {
                tokens.push({ k: 'cmp', text: '==' });
                i += 2;
                continue;
            }
            return { error: ERR_BAD_CHAR }; // lone `=`
        }
        if (c === '!') {
            if (src[i + 1] === '=') {
                tokens.push({ k: 'cmp', text: '!=' });
                i += 2;
                continue;
            }
            tokens.push({ k: 'op', text: '!' }); // unary not
            i++;
            continue;
        }
        if (c === '<' || c === '>') {
            if (src[i + 1] === '=') {
                tokens.push({ k: 'cmp', text: c + '=' });
                i += 2;
                continue;
            }
            tokens.push({ k: 'cmp', text: c });
            i++;
            continue;
        }
        if (isIdentifierStart(c)) {
            let j: number = i;
            while (j < n && isIdentifierPart(src[j])) j++;
            tokens.push({ k: 'ident', text: src.slice(i, j) });
            i = j;
            continue;
        }

        return { error: ERR_BAD_CHAR };
    }

    return { tokens };
};

// Recursive-descent grammar check — mirrors lv_xml_eval_top()/cmp()/add()/…
// Grammar (see lv_xml_evaluate.c header comment):
//   top     := cmp ( '?' branch ':' branch | ('.' cmp)* )
//   branch  := cmp ('.' cmp)*
//   cmp     := add ( relop add )?            (at most one comparison)
//   add     := term ( ('+'|'-') term )*
//   term    := unary ( ('*'|'/'|'%') unary )*
//   unary   := ('-'|'+'|'!') unary | primary
//   primary := NUMBER | HEX | STRING | IDENT | '(' cmp ')'
// Returns an error string, or null when the token stream is well-formed.
const parseExpressionGrammar = (tokens: ExprToken[]): string | null => {
    let pos: number = 0;
    let error: string | null = null;

    const peek = (): ExprToken | undefined => tokens[pos];
    const fail = (message: string): void => {
        if (error === null) error = message;
    };
    const isOp = (t: ExprToken | undefined, text: string): boolean => !!t && t.k === 'op' && t.text === text;

    const primary = (): void => {
        if (error) return;
        const t: ExprToken | undefined = peek();
        if (!t) {
            fail(ERR_EXPECTED_OPERAND);
            return;
        }
        if (t.k === 'num' || t.k === 'hex' || t.k === 'str' || t.k === 'ident') {
            pos++;
            return;
        }
        if (t.k === 'lparen') {
            pos++;
            cmp(); // only arithmetic/comparison inside parens (no concat/ternary)
            if (error) return;
            if (peek()?.k !== 'rparen') {
                fail(ERR_EXPECTED_RPAREN);
                return;
            }
            pos++;
            return;
        }
        fail(ERR_EXPECTED_OPERAND);
    };

    const unary = (): void => {
        if (error) return;
        const t: ExprToken | undefined = peek();
        if (isOp(t, '-') || isOp(t, '+') || isOp(t, '!')) {
            pos++;
            unary();
            return;
        }
        primary();
    };

    const term = (): void => {
        unary();
        while (!error && (isOp(peek(), '*') || isOp(peek(), '/') || isOp(peek(), '%'))) {
            pos++;
            unary();
        }
    };

    const add = (): void => {
        term();
        while (!error && (isOp(peek(), '+') || isOp(peek(), '-'))) {
            pos++;
            term();
        }
    };

    const cmp = (): void => {
        add();
        if (error) return;
        if (peek()?.k === 'cmp') {
            pos++;
            add(); // at most one comparison; a second one falls through to "trailing"
        }
    };

    const branch = (): void => {
        cmp();
        while (!error && isOp(peek(), '.')) {
            pos++;
            cmp();
        }
    };

    const top = (): void => {
        cmp(); // the condition, or the first concat operand
        if (error) return;

        if (isOp(peek(), '?')) {
            pos++;
            branch();
            if (error) return;
            if (!isOp(peek(), ':')) {
                fail(ERR_EXPECTED_COLON);
                return;
            }
            pos++;
            branch();
            return;
        }

        // A leading `.` means the whole thing was a concatenation.
        while (!error && isOp(peek(), '.')) {
            pos++;
            cmp();
        }
    };

    top();
    if (error) return error;
    if (pos < tokens.length) return ERR_TRAILING;
    return null;
};

/**
 * Validate the grammar of a `{ ... }` expression (syntax only — see the
 * DUPLICATION WARNING above). Returns `{ isValid: true }` for a well-formed
 * expression, or a specific reason for the first structural error. Non-brace
 * values are treated as valid (not this function's concern).
 */
export const validateExpressionSyntax = (value: string): { isValid: boolean; message?: string } => {
    const trimmed: string = value.trim();
    if (!isExpression(trimmed)) return { isValid: true };

    const inner: string = trimmed.slice(1, -1).trim();
    if (inner.length === 0) return { isValid: false, message: ERR_EMPTY };

    const tokenized: TokenizeResult = tokenizeExpression(inner);
    if ('error' in tokenized) return { isValid: false, message: tokenized.error };

    const grammarError: string | null = parseExpressionGrammar(tokenized.tokens);
    if (grammarError) return { isValid: false, message: grammarError };

    return { isValid: true };
};
